import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ChefHat, Plus, Trash2, Edit, Play, Package, FlaskConical, X, BookOpen, ClipboardList, Wand2 } from 'lucide-react';
import api from '@/lib/api';
import { useBranchStore } from '@/stores';
import { Sparkline } from '@/components/dashboard/Sparkline';
import { toast } from 'sonner';
import { RecordWasteDialog } from '@/components/stock/RecordWasteDialog';

export default function RecipesPage() {
  const [recipes, setRecipes] = useState([]);
  const [items, setItems] = useState([]);
  const [productions, setProductions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editRecipe, setEditRecipe] = useState(null);
  const [showProduce, setShowProduce] = useState(null);
  const [produceQty, setProduceQty] = useState('');
  const [produceBranch, setProduceBranch] = useState('');
  const [produceNotes, setProduceNotes] = useState('');
  const [produceBalances, setProduceBalances] = useState([]);
  const _today = new Date();
  const [prodStart, setProdStart] = useState(new Date(_today.getFullYear(), _today.getMonth(), 1).toISOString().slice(0, 10));
  const [prodEnd, setProdEnd] = useState(_today.toISOString().slice(0, 10));
  const [prodBranchFilter, setProdBranchFilter] = useState('all');
  const [costHistory, setCostHistory] = useState({});
  const { branches } = useBranchStore();
  const navigate = useNavigate();
  const [wasteCtx, setWasteCtx] = useState(null); // { itemId, branchId } for shared Record Waste dialog
  const [form, setForm] = useState({ name: '', description: '', category: 'general', output_qty: 1, output_unit: 'piece', output_item_id: '', ingredients: [{ item_id: '', quantity_per_unit: '', input_unit: '' }] });

  // iter876 — Auto-link Menu → Stock UX
  const [autoLinkOpen, setAutoLinkOpen] = useState(false);
  const [autoLinkPreview, setAutoLinkPreview] = useState(null);
  const [autoLinkLoading, setAutoLinkLoading] = useState(false);
  const [defaultQty, setDefaultQty] = useState(1);

  const openAutoLinkPreview = async () => {
    setAutoLinkOpen(true);
    setAutoLinkLoading(true);
    setAutoLinkPreview(null);
    try {
      const { data } = await api.post('/recipes/auto-link', { dry_run: true, default_qty: defaultQty });
      setAutoLinkPreview(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Preview failed'); setAutoLinkOpen(false); }
    finally { setAutoLinkLoading(false); }
  };

  const commitAutoLink = async () => {
    setAutoLinkLoading(true);
    try {
      const { data } = await api.post('/recipes/auto-link', { dry_run: false, default_qty: defaultQty });
      toast.success(`Created ${data.summary.created} recipe${data.summary.created === 1 ? '' : 's'}. Sales will now deduct stock automatically.`);
      setAutoLinkOpen(false);
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Auto-link failed'); }
    finally { setAutoLinkLoading(false); }
  };

  const fetchAll = async () => {
    try {
      const [rRes, iRes, pRes] = await Promise.all([
        api.get('/recipes'), api.get('/items'), api.get('/production-entries'),
      ]);
      setRecipes(rRes.data || []);
      setItems(iRes.data || []);
      setProductions(pRes.data || []);
    } catch { toast.error('Failed to load data'); }
  };
  useEffect(() => { fetchAll(); }, []);

  // Cost Sparkline — load recent per-ingredient purchase costs so the Recipe
  // view can show price creep. Reuses stock_entries via /recipes/cost-history.
  useEffect(() => {
    const ids = Array.from(new Set((recipes || []).flatMap(r => (r.ingredients || []).map(i => i.item_id)).filter(Boolean)));
    if (!ids.length) { setCostHistory({}); return; }
    api.get('/recipes/cost-history', { params: { item_ids: ids.join(',') } })
      .then(r => setCostHistory(r.data || {}))
      .catch(() => setCostHistory({}));
  }, [recipes]);

  // Low-Stock Alert — load current balances (with min levels) for the branch
  // the batch is produced at, so the Save Production dialog can warn when a
  // batch would push any ingredient below its minimum. Reuses /stock/balance.
  useEffect(() => {
    if (!showProduce) { setProduceBalances([]); return; }
    api.get('/stock/balance', { params: produceBranch ? { branch_id: produceBranch } : {} })
      .then(r => setProduceBalances(r.data || []))
      .catch(() => setProduceBalances([]));
  }, [showProduce, produceBranch]);

  // Ingredients this batch would push below their minimum stock level.
  const lowStockWarnings = (() => {
    if (!showProduce || !(parseFloat(produceQty) > 0)) return [];
    const balByItem = {};
    (produceBalances || []).forEach(b => { balByItem[b.item_id] = b; });
    const out = [];
    (showProduce.ingredients || []).forEach(ing => {
      const required = (ing.quantity_per_unit || 0) * parseFloat(produceQty) / (showProduce.output_qty || 1);
      const bal = balByItem[ing.item_id];
      const current = bal ? Number(bal.balance || 0) : 0;
      const minLvl = bal ? Number(bal.min_level || 0) : 0;
      const projected = current - required;
      if (projected < 0 || (minLvl > 0 && projected < minLvl)) {
        out.push({
          name: ing.item_name, unit: ing.unit,
          projected: projected, min: minLvl, out: projected < 0,
        });
      }
    });
    return out;
  })();

  // Batch Cost Report — period + branch summary of value created. Reuses the
  // productions list (each entry carries total_cost + branch_id from Phase 2).
  const branchName = (id) => (branches.find(b => b.id === id) || {}).name || (id ? 'Unknown' : 'No branch');
  const prodFiltered = (productions || []).filter(p => {
    const d = (p.produced_at || '').slice(0, 10);
    if (prodStart && d < prodStart) return false;
    if (prodEnd && d > prodEnd) return false;
    if (prodBranchFilter !== 'all' && (p.branch_id || '') !== prodBranchFilter) return false;
    return true;
  });
  const prodSummary = (() => {
    const byBranch = {};
    let totalValue = 0, totalBatches = 0;
    prodFiltered.forEach(p => {
      const bid = p.branch_id || '';
      const val = Number(p.total_cost || 0);
      totalValue += val; totalBatches += 1;
      if (!byBranch[bid]) byBranch[bid] = { batches: 0, value: 0 };
      byBranch[bid].batches += 1; byBranch[bid].value += val;
    });
    const rows = Object.entries(byBranch)
      .map(([bid, v]) => ({ branch_id: bid, name: branchName(bid), ...v }))
      .sort((a, b) => b.value - a.value);
    return { totalValue, totalBatches, rows };
  })();


  const resetForm = () => setForm({ name: '', description: '', category: 'general', output_qty: 1, output_unit: 'piece', output_item_id: '', ingredients: [{ item_id: '', quantity_per_unit: '', input_unit: '' }] });

  const openEdit = (r) => {
    setEditRecipe(r);
    setForm({ name: r.name, description: r.description || '', category: r.category || 'general', output_qty: r.output_qty || 1, output_unit: r.output_unit || 'piece', output_item_id: r.output_item_id || '', ingredients: r.ingredients.map(i => ({ item_id: i.item_id, quantity_per_unit: i.quantity_per_unit, input_unit: i.input_unit || '' })) });
    setShowForm(true);
  };

  const addIngredient = () => setForm(f => ({ ...f, ingredients: [...f.ingredients, { item_id: '', quantity_per_unit: '', input_unit: '' }] }));
  const removeIngredient = (idx) => setForm(f => ({ ...f, ingredients: f.ingredients.filter((_, i) => i !== idx) }));
  const updateIngredient = (idx, field, val) => setForm(f => ({ ...f, ingredients: f.ingredients.map((ing, i) => i === idx ? { ...ing, [field]: val } : ing) }));

  // iter877 — Unit conversion helper. When the operator types an ingredient
  // qty in a non-base unit (e.g. "200 g" but item is tracked in "kg"),
  // convert to the item's base unit before storing.
  // Known SI conversions + per-item pack_units are used.
  const SI_TO_BASE = {
    kg: { base: 'gram', factor: 1000 },
    g: { base: 'gram', factor: 1 }, gram: { base: 'gram', factor: 1 },
    l: { base: 'ml', factor: 1000 },
    ml: { base: 'ml', factor: 1 },
    dozen: { base: 'piece', factor: 12 },
    piece: { base: 'piece', factor: 1 }, pc: { base: 'piece', factor: 1 }, pcs: { base: 'piece', factor: 1 },
  };
  const norm = (u) => (u || '').trim().toLowerCase();

  const availableUnitsForItem = (item) => {
    if (!item) return [];
    const base = norm(item.unit) || 'piece';
    const opts = [{ name: base, factor_to_base: 1, is_base: true }];
    // Include SI counterparts of the base (kg for gram, l for ml, dozen for piece).
    for (const [u, v] of Object.entries(SI_TO_BASE)) {
      if (v.base === base && u !== base && !opts.some(o => o.name === u)) {
        opts.push({ name: u, factor_to_base: v.factor });
      }
    }
    // Custom pack_units on the item.
    for (const pu of (item.pack_units || [])) {
      if (pu?.name && !opts.some(o => norm(o.name) === norm(pu.name))) {
        opts.push({ name: pu.name, factor_to_base: Number(pu.factor_to_base) || 1 });
      }
    }
    return opts;
  };

  const convertToBase = (qty, inputUnit, item) => {
    if (!item || !qty) return { base_qty: qty, base_unit: item?.unit || 'piece' };
    const base = norm(item.unit) || 'piece';
    const u = norm(inputUnit) || base;
    if (u === base) return { base_qty: Number(qty), base_unit: base };
    // SI table
    const si = SI_TO_BASE[u];
    if (si && si.base === base) return { base_qty: Number(qty) * si.factor, base_unit: base };
    // pack_units
    const pu = (item.pack_units || []).find(p => norm(p.name) === u);
    if (pu?.factor_to_base) return { base_qty: Number(qty) * Number(pu.factor_to_base), base_unit: base };
    return { base_qty: Number(qty), base_unit: base }; // unknown conversion — pass through
  };

  const saveRecipe = async () => {
    if (!form.name.trim()) { toast.error('Recipe name required'); return; }
    const validIngs = form.ingredients.filter(i => i.item_id && parseFloat(i.quantity_per_unit) > 0);
    if (!validIngs.length) { toast.error('At least one ingredient with quantity required'); return; }
    // iter877 — Convert every ingredient qty to the item's base unit before saving.
    const payload = {
      ...form,
      ingredients: validIngs.map(i => {
        const item = items.find(x => x.id === i.item_id);
        const { base_qty, base_unit } = convertToBase(parseFloat(i.quantity_per_unit), i.input_unit, item);
        return {
          item_id: i.item_id,
          quantity_per_unit: base_qty,
          unit: base_unit,
          input_unit: i.input_unit || base_unit,
          input_qty: parseFloat(i.quantity_per_unit),
        };
      }),
      output_qty: parseFloat(form.output_qty) || 1,
    };
    try {
      if (editRecipe?.id) { await api.put(`/recipes/${editRecipe.id}`, payload); toast.success('Recipe updated'); }
      else { await api.post('/recipes', payload); toast.success('Recipe created'); }
      setShowForm(false); resetForm(); setEditRecipe(null); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const deleteRecipe = async (id) => {
    if (!window.confirm('Delete this recipe?')) return;
    try { await api.delete(`/recipes/${id}`); toast.success('Deleted'); fetchAll(); } catch { toast.error('Failed'); }
  };

  const submitProduction = async () => {
    if (!showProduce || !produceQty || parseFloat(produceQty) <= 0) { toast.error('Enter valid quantity'); return; }
    if (!produceBranch) { toast.error('Select a branch'); return; }
    try {
      const res = await api.post('/production-entries', { recipe_id: showProduce.id, quantity_produced: parseFloat(produceQty), branch_id: produceBranch, notes: produceNotes });
      toast.success(res.data.message);
      setShowProduce(null); setProduceQty(''); setProduceBranch(''); setProduceNotes(''); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="recipes-page">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-lg shadow-orange-200/50">
              <FlaskConical size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="recipes-title">Recipes & Production</h1>
              <p className="text-sm text-muted-foreground mt-0.5">Manage recipes, ingredients, and auto-deduct stock on production</p>
            </div>
          </div>
          <Button onClick={() => { resetForm(); setEditRecipe(null); setShowForm(true); }} className="bg-orange-500 hover:bg-orange-600 rounded-full h-9 text-xs" data-testid="create-recipe-btn">
            <Plus size={14} className="mr-1" /> New Recipe
          </Button>
          <Button
            variant="outline"
            onClick={openAutoLinkPreview}
            className="rounded-full h-9 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid="auto-link-btn"
          >
            <Wand2 size={14} className="mr-1" /> Auto-link Menu → Stock
          </Button>
        </div>

        {/* iter876 — Auto-link preview + commit dialog */}
        <Dialog open={autoLinkOpen} onOpenChange={setAutoLinkOpen}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="auto-link-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wand2 size={18} className="text-emerald-600" /> Auto-link Menu Items to Stock
              </DialogTitle>
            </DialogHeader>
            {autoLinkLoading ? (
              <div className="py-12 text-center text-sm text-muted-foreground">Scanning your menu items and stock…</div>
            ) : autoLinkPreview ? (
              <div className="flex-1 overflow-y-auto space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                  <div className="p-2 rounded-lg bg-emerald-50 border border-emerald-200"><div className="text-emerald-700 font-bold text-lg">{autoLinkPreview.summary.created}</div><div className="text-emerald-800">Will be linked</div></div>
                  <div className="p-2 rounded-lg bg-blue-50 border border-blue-200"><div className="text-blue-700 font-bold text-lg">{autoLinkPreview.summary.already_linked}</div><div className="text-blue-800">Already linked</div></div>
                  <div className="p-2 rounded-lg bg-amber-50 border border-amber-200"><div className="text-amber-700 font-bold text-lg">{autoLinkPreview.summary.ambiguous}</div><div className="text-amber-800">Need choice</div></div>
                  <div className="p-2 rounded-lg bg-rose-50 border border-rose-200"><div className="text-rose-700 font-bold text-lg">{autoLinkPreview.summary.no_match}</div><div className="text-rose-800">No match</div></div>
                </div>

                <div className="text-xs text-muted-foreground">
                  <b>Ingredient qty per sale:</b>
                  <input type="number" step="0.01" min="0.01" value={defaultQty}
                    onChange={(e) => setDefaultQty(parseFloat(e.target.value) || 1)}
                    className="ml-2 w-24 h-7 px-2 border rounded text-sm" data-testid="auto-link-qty" />
                  <span className="ml-2 opacity-75">(deducted from stock every time a menu item is sold)</span>
                </div>

                {autoLinkPreview.created.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-emerald-700 mb-1">Will create ({autoLinkPreview.created.length})</div>
                    <div className="border rounded-lg divide-y max-h-40 overflow-y-auto">
                      {autoLinkPreview.created.map(r => (
                        <div key={r.menu_item_id} className="px-3 py-1.5 text-xs flex justify-between">
                          <span>{r.name}</span>
                          <span className="text-muted-foreground">→ {r.ingredient_name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {autoLinkPreview.ambiguous.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-amber-700 mb-1">Ambiguous — multiple stock items match ({autoLinkPreview.ambiguous.length})</div>
                    <div className="border rounded-lg divide-y max-h-40 overflow-y-auto">
                      {autoLinkPreview.ambiguous.map(r => (
                        <div key={r.menu_item_id} className="px-3 py-1.5 text-xs">
                          <div className="font-medium">{r.name}</div>
                          <div className="text-muted-foreground text-[11px]">Candidates: {r.candidates.map(c => c.name).join(', ')} — create the recipe manually from the New Recipe button.</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {autoLinkPreview.no_match.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-rose-700 mb-1">No matching stock item ({autoLinkPreview.no_match.length})</div>
                    <div className="border rounded-lg max-h-32 overflow-y-auto p-2 text-[11px] text-muted-foreground">
                      {autoLinkPreview.no_match.map(r => r.name).join(' · ')}
                    </div>
                    <div className="text-[11px] mt-1 text-muted-foreground">Create a stock item with the same name at <a href="/items" className="underline">Inventory</a>, then re-run.</div>
                  </div>
                )}
              </div>
            ) : null}
            <DialogFooter>
              <Button variant="outline" onClick={() => setAutoLinkOpen(false)} data-testid="auto-link-cancel">Cancel</Button>
              <Button
                onClick={commitAutoLink}
                disabled={!autoLinkPreview || autoLinkLoading || (autoLinkPreview?.summary.created || 0) === 0}
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                data-testid="auto-link-commit"
              >
                Create {autoLinkPreview?.summary.created || 0} recipe{(autoLinkPreview?.summary.created || 0) === 1 ? '' : 's'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Tabs defaultValue="recipes" className="w-full">
          <TabsList>
            <TabsTrigger value="recipes" data-testid="tab-recipes"><BookOpen size={14} className="mr-1" /> Recipes ({recipes.length})</TabsTrigger>
            <TabsTrigger value="production" data-testid="tab-production"><ClipboardList size={14} className="mr-1" /> Production Log ({productions.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="recipes">
            {recipes.length === 0 ? (
              <Card><CardContent className="py-12 text-center"><FlaskConical size={40} className="mx-auto text-muted-foreground mb-3" /><p className="text-sm text-muted-foreground">No recipes yet. Create your first one!</p></CardContent></Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="recipes-grid">
                {recipes.map(r => (
                  <Card key={r.id} className="hover:shadow-md transition-shadow" data-testid={`recipe-${r.id}`}>
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-sm font-outfit">{r.name}</CardTitle>
                          <p className="text-[11px] text-muted-foreground mt-0.5">{r.description || 'No description'}</p>
                        </div>
                        <Badge variant="outline" className="text-[10px] capitalize">{r.category}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="mb-2">
                        <p className="text-[10px] text-muted-foreground mb-1">Ingredients ({r.ingredients?.length || 0})</p>
                        <div className="flex flex-wrap gap-1">
                          {(r.ingredients || []).slice(0, 5).map((ing, i) => (
                            <Badge key={i} className="bg-muted text-muted-foreground text-[9px]" title={ing.unit_cost != null ? `unit cost: ${ing.unit_cost?.toFixed(4)} · line: ${ing.line_cost?.toFixed(2)}` : ''}>
                              {ing.item_name}: {ing.quantity_per_unit} {ing.unit}
                              {ing.line_cost != null && <span className="ml-1 opacity-70">({Number(ing.line_cost).toFixed(2)})</span>}
                              {(() => {
                                const vals = (costHistory[ing.item_id] || []).map(x => x.unit_cost);
                                if (vals.length < 2) return null;
                                const rising = vals[vals.length - 1] > vals[0];
                                return (
                                  <span className="ml-1 inline-flex align-middle" title={`Cost trend: ${vals[0].toFixed(3)} → ${vals[vals.length - 1].toFixed(3)}`} data-testid={`ingredient-cost-spark-${ing.item_id}`}>
                                    <Sparkline data={vals} color={rising ? '#F43F5E' : '#22C55E'} width={28} height={10} />
                                  </span>
                                );
                              })()}
                            </Badge>
                          ))}
                          {(r.ingredients || []).length > 5 && <Badge className="bg-muted text-muted-foreground text-[9px]">+{r.ingredients.length - 5}</Badge>}
                        </div>
                      </div>
                      <p className="text-[10px] text-muted-foreground mb-1">Makes: {r.output_qty} {r.output_unit}</p>
                      {(r.total_cost != null) && (
                        <div className="flex items-center justify-between mb-3 px-2 py-1.5 rounded bg-emerald-50 border border-emerald-100" data-testid={`recipe-cost-${r.id}`}>
                          <span className="text-[10px] text-emerald-700 font-medium">Total cost</span>
                          <span className="text-[11px] font-bold text-emerald-800">{Number(r.total_cost).toFixed(2)} <span className="opacity-60 font-normal">· {Number(r.cost_per_unit).toFixed(2)}/{r.output_unit}</span></span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Button size="sm" className="bg-success hover:bg-success h-7 text-[11px] flex-1" onClick={() => setShowProduce(r)} data-testid={`produce-${r.id}`}>
                          <ChefHat size={11} className="mr-1" /> Produce
                        </Button>
                        <Button variant="outline" size="sm" className="h-7 px-2" onClick={() => openEdit(r)} data-testid={`edit-recipe-${r.id}`}><Edit size={11} /></Button>
                        <Button variant="outline" size="sm" className="h-7 px-2 text-error" onClick={() => deleteRecipe(r.id)} data-testid={`delete-recipe-${r.id}`}><Trash2 size={11} /></Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="production">
            {productions.length === 0 ? (
              <Card><CardContent className="py-12 text-center"><ClipboardList size={40} className="mx-auto text-muted-foreground mb-3" /><p className="text-sm text-muted-foreground">No production entries yet. Produce a recipe to see logs.</p></CardContent></Card>
            ) : (
              <div className="space-y-3" data-testid="production-log">
                {/* Batch Cost Report — period + branch summary of value created */}
                <Card data-testid="batch-cost-report">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                      <div>
                        <Label className="text-[11px] text-muted-foreground">From</Label>
                        <Input type="date" value={prodStart} onChange={e => setProdStart(e.target.value)} className="mt-1 h-8 text-xs" data-testid="batch-report-start" />
                      </div>
                      <div>
                        <Label className="text-[11px] text-muted-foreground">To</Label>
                        <Input type="date" value={prodEnd} onChange={e => setProdEnd(e.target.value)} className="mt-1 h-8 text-xs" data-testid="batch-report-end" />
                      </div>
                      <div className="min-w-[160px]">
                        <Label className="text-[11px] text-muted-foreground">Branch</Label>
                        <Select value={prodBranchFilter} onValueChange={setProdBranchFilter}>
                          <SelectTrigger className="mt-1 h-8 text-xs" data-testid="batch-report-branch"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All branches</SelectItem>
                            {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      <div className="rounded-lg bg-stone-50 dark:bg-stone-900/40 p-3">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Batches</p>
                        <p className="text-lg font-bold" data-testid="batch-report-batches">{prodSummary.totalBatches}</p>
                      </div>
                      <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/20 p-3">
                        <p className="text-[10px] text-emerald-700 uppercase tracking-wide">Value Created</p>
                        <p className="text-lg font-bold text-emerald-800 dark:text-emerald-300" data-testid="batch-report-value">SR {prodSummary.totalValue.toFixed(2)}</p>
                      </div>
                      <div className="rounded-lg bg-stone-50 dark:bg-stone-900/40 p-3">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Branches</p>
                        <p className="text-lg font-bold">{prodSummary.rows.length}</p>
                      </div>
                    </div>
                    {prodSummary.rows.length > 0 && (
                      <div className="border rounded-lg divide-y">
                        {prodSummary.rows.map(r => (
                          <div key={r.branch_id || 'none'} className="flex justify-between items-center px-3 py-1.5 text-xs" data-testid={`batch-report-row-${r.branch_id || 'none'}`}>
                            <span className="text-muted-foreground">{r.name} <span className="opacity-60">· {r.batches} batch{r.batches === 1 ? '' : 'es'}</span></span>
                            <span className="font-semibold text-emerald-700">SR {r.value.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
                {prodFiltered.length === 0 && <div className="text-center py-8 text-xs text-muted-foreground">No production in this period/branch.</div>}
                {prodFiltered.map(p => (
                  <Card key={p.id} data-testid={`production-${p.id}`}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-sm">{p.recipe_name} <span className="text-orange-600 font-bold">x{p.quantity_produced}</span></p>
                          <p className="text-xs text-muted-foreground mt-0.5">{new Date(p.produced_at).toLocaleString()} | by {p.created_by_name}</p>
                          {p.notes && <p className="text-xs text-muted-foreground mt-1">{p.notes}</p>}
                        </div>
                        <Badge className="bg-success/20 text-success text-[10px]">{p.ingredients_used?.length || 0} items deducted</Badge>
                      </div>
                      {(p.total_cost != null) && (
                        <div className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-emerald-50 border border-emerald-100 px-2 py-1" data-testid={`production-value-${p.id}`}>
                          <span className="text-[10px] text-emerald-700">Value Added</span>
                          <span className="text-[11px] font-bold text-emerald-800">SR {Number(p.total_cost).toFixed(2)}</span>
                          {p.yield_added && <span className="text-[10px] text-emerald-600">· into stock</span>}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-1 mt-2">
                        {(p.ingredients_used || []).map((ing, i) => (
                          <Badge key={i} variant="outline" className="text-[9px]">{ing.item_name}: -{ing.quantity_used} {ing.unit}</Badge>
                        ))}
                      </div>
                      <div className="mt-2">
                        <Button variant="outline" size="sm" className="h-7 gap-1 text-xs text-error border-error/30 hover:bg-error/5"
                          data-testid={`production-record-waste-${p.id}`}
                          onClick={() => setWasteCtx({
                            itemId: (recipes.find(r => r.id === p.recipe_id)?.output_item_id) || '',
                            branchId: p.branch_id || '',
                          })}>
                          <Trash2 size={12} /> Record Waste
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Create/Edit Recipe Dialog */}
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="max-w-lg max-h-[90dvh] overflow-y-auto" data-testid="recipe-form-dialog">
            <DialogHeader><DialogTitle className="font-outfit">{editRecipe ? 'Edit' : 'Create'} Recipe</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Recipe Name *</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="mt-1" data-testid="recipe-name" /></div>
                <div><Label className="text-xs">Category</Label>
                  <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                    <SelectTrigger className="mt-1" data-testid="recipe-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['general', 'food', 'beverage', 'bakery', 'dessert', 'sauce', 'prep'].map(c => (
                        <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label className="text-xs">Description</Label><Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="mt-1" data-testid="recipe-desc" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Output Qty</Label><Input type="number" value={form.output_qty} onChange={e => setForm(f => ({ ...f, output_qty: e.target.value }))} className="mt-1" data-testid="recipe-output-qty" /></div>
                <div><Label className="text-xs">Output Unit</Label><Input value={form.output_unit} onChange={e => setForm(f => ({ ...f, output_unit: e.target.value }))} className="mt-1" data-testid="recipe-output-unit" /></div>
              </div>

              {/* Phase 2 (Output & Yield) — optional finished-good stock item.
                  When set, producing this recipe adds the yield back into
                  inventory, valued at its auto-computed ingredient cost. */}
              <div>
                <Label className="text-xs">Adds to stock item (optional)</Label>
                <Select value={form.output_item_id || 'none'} onValueChange={v => setForm(f => ({ ...f, output_item_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger className="mt-1" data-testid="recipe-output-item"><SelectValue placeholder="No stock output" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No stock output</SelectItem>
                    {items.map(it => <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground mt-1">Producing a batch will add the yield to this stock item (auto-costed).</p>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label className="text-xs font-bold">Ingredients</Label>
                  <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={addIngredient} data-testid="add-ingredient-btn"><Plus size={10} className="mr-1" /> Add</Button>
                </div>
                <div className="space-y-2">
                  {form.ingredients.map((ing, idx) => {
                    const chosenItem = items.find(x => x.id === ing.item_id);
                    const unitOpts = availableUnitsForItem(chosenItem);
                    const activeUnit = ing.input_unit || (chosenItem?.unit || 'piece');
                    const preview = chosenItem && parseFloat(ing.quantity_per_unit) > 0
                      ? convertToBase(parseFloat(ing.quantity_per_unit), activeUnit, chosenItem)
                      : null;
                    const showConvHint = preview && chosenItem && (activeUnit !== (chosenItem.unit || 'piece'));
                    return (
                    <div key={idx} className="space-y-1" data-testid={`ingredient-row-${idx}`}>
                      <div className="flex gap-2 items-center">
                        <Select value={ing.item_id || 'none'} onValueChange={v => {
                          updateIngredient(idx, 'item_id', v === 'none' ? '' : v);
                          // Reset input_unit to the picked item's base unit.
                          const picked = items.find(x => x.id === v);
                          updateIngredient(idx, 'input_unit', picked?.unit || 'piece');
                        }}>
                          <SelectTrigger className="flex-1 text-xs h-8"><SelectValue placeholder="Select item" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none" disabled>Select item...</SelectItem>
                            {items.map(it => <SelectItem key={it.id} value={it.id}>{it.name} ({it.unit})</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Input type="number" placeholder="Qty" value={ing.quantity_per_unit} onChange={e => updateIngredient(idx, 'quantity_per_unit', e.target.value)} className="w-20 text-xs h-8" data-testid={`ingredient-qty-${idx}`} />
                        {chosenItem && unitOpts.length > 1 ? (
                          <Select value={activeUnit} onValueChange={v => updateIngredient(idx, 'input_unit', v)}>
                            <SelectTrigger className="w-20 text-xs h-8" data-testid={`ingredient-unit-${idx}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {unitOpts.map(o => <SelectItem key={o.name} value={o.name}>{o.name}{o.is_base ? ' (base)' : ''}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="w-20 text-[10px] text-muted-foreground text-center">{chosenItem?.unit || ''}</span>
                        )}
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-400 hover:text-error" onClick={() => removeIngredient(idx)}><X size={12} /></Button>
                      </div>
                      {showConvHint && (
                        <div className="text-[10px] text-emerald-700 pl-1" data-testid={`ingredient-conv-${idx}`}>
                          = {Number(preview.base_qty).toLocaleString(undefined, { maximumFractionDigits: 4 })} {preview.base_unit} (stock is tracked in {chosenItem.unit})
                        </div>
                      )}
                    </div>
                  );})}
                </div>
              </div>
            </div>
            <DialogFooter className="gap-2 mt-4">
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button onClick={saveRecipe} className="bg-orange-500 hover:bg-orange-600" data-testid="save-recipe-btn">Save Recipe</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Production Dialog */}
        <Dialog open={!!showProduce} onOpenChange={() => setShowProduce(null)}>
          <DialogContent data-testid="production-dialog">
            <DialogHeader><DialogTitle className="font-outfit flex items-center gap-2"><ChefHat size={18} className="text-success" /> Record Production</DialogTitle></DialogHeader>
            {showProduce && (
              <div className="space-y-4">
                <div className="p-3 bg-success/10 rounded-lg border border-success/30">
                  <p className="font-medium text-sm">{showProduce.name}</p>
                  <p className="text-xs text-muted-foreground mt-1">Recipe makes {showProduce.output_qty} {showProduce.output_unit}</p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(showProduce.ingredients || []).map((ing, i) => (
                      <Badge key={i} variant="outline" className="text-[9px]">{ing.item_name}: {ing.quantity_per_unit} {ing.unit}</Badge>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Quantity Produced *</Label><Input type="number" value={produceQty} onChange={e => setProduceQty(e.target.value)} placeholder="e.g. 10" className="mt-1" data-testid="produce-qty" /></div>
                  <div><Label className="text-xs">Branch *</Label>
                    <Select value={produceBranch || undefined} onValueChange={setProduceBranch}>
                      <SelectTrigger className="mt-1" data-testid="produce-branch"><SelectValue placeholder="Select branch" /></SelectTrigger>
                      <SelectContent>
                        {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {produceQty > 0 && (
                  <div className="p-3 bg-warning/10 rounded-lg border border-warning/30">
                    <p className="text-xs font-bold text-warning mb-1">Ingredients to be deducted:</p>
                    {(showProduce.ingredients || []).map((ing, i) => {
                      const used = (ing.quantity_per_unit * parseFloat(produceQty || 0) / (showProduce.output_qty || 1)).toFixed(2);
                      return <p key={i} className="text-xs text-warning">{ing.item_name}: <span className="font-bold">-{used} {ing.unit}</span></p>;
                    })}
                  </div>
                )}
                {lowStockWarnings.length > 0 && (
                  <div className="p-3 bg-red-50 rounded-lg border border-red-300" data-testid="production-low-stock-warning">
                    <p className="text-xs font-bold text-red-600 mb-1">⚠ Low stock warning</p>
                    {lowStockWarnings.map((w, i) => (
                      <div key={i} className="flex items-center justify-between gap-2">
                        <p className="text-xs text-red-600">
                          {w.name}: {w.out
                            ? <span className="font-bold">not enough stock (short {Math.abs(w.projected).toFixed(2)} {w.unit})</span>
                            : <span className="font-bold">would drop to {w.projected.toFixed(2)} {w.unit}, below minimum {w.min} {w.unit}</span>}
                        </p>
                        <button
                          type="button"
                          onClick={() => navigate(`/purchase-orders?new=1&item_name=${encodeURIComponent(w.name)}&unit=${encodeURIComponent(w.unit || '')}`)}
                          className="text-[11px] font-semibold text-red-700 underline underline-offset-2 hover:text-red-800 shrink-0"
                          data-testid={`reorder-link-${i}`}
                        >
                          Reorder →
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div><Label className="text-xs">Notes</Label><Textarea value={produceNotes} onChange={e => setProduceNotes(e.target.value)} className="mt-1 h-16" data-testid="produce-notes" /></div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowProduce(null)}>Cancel</Button>
              <Button onClick={submitProduction} className="bg-success hover:bg-success" data-testid="submit-production-btn">Record Production</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <RecordWasteDialog
          open={!!wasteCtx}
          onOpenChange={(v) => { if (!v) setWasteCtx(null); }}
          items={items}
          branches={branches}
          defaultItemId={wasteCtx?.itemId || ''}
          defaultBranchId={wasteCtx?.branchId || ''}
          onRecorded={() => { setWasteCtx(null); fetchAll(); }}
        />
      </div>
    </DashboardLayout>
  );
}
